/**
 * Central utility for clearing module-level caches on auth state changes.
 * 
 * IMPORTANT: This prevents stale data from one user account leaking to another.
 * Module-level caches persist outside React's lifecycle and are NOT cleared
 * when React Query cache is cleared.
 * 
 * Call clearAllAuthCaches() on SIGNED_OUT and SIGNED_IN events.
 */

// Registry of cache clear functions from various modules
const cacheClearFunctions: Array<() => void> = [];

/**
 * Register a cache clear function to be called on auth state changes.
 * Components with module-level caches should call this on module load.
 */
export function registerCacheClearFunction(clearFn: () => void): void {
  if (!cacheClearFunctions.includes(clearFn)) {
    cacheClearFunctions.push(clearFn);
  }
}

/**
 * Unregister a cache clear function (typically not needed, but available).
 */
export function unregisterCacheClearFunction(clearFn: () => void): void {
  const index = cacheClearFunctions.indexOf(clearFn);
  if (index > -1) {
    cacheClearFunctions.splice(index, 1);
  }
}

/**
 * Clear all registered module-level caches.
 * Should be called on SIGNED_OUT and SIGNED_IN auth events.
 */
export function clearAllAuthCaches(): void {
  console.log('[AuthCacheClear] Clearing all module-level caches...');
  cacheClearFunctions.forEach((clearFn) => {
    try {
      clearFn();
    } catch (error) {
      console.error('[AuthCacheClear] Error clearing cache:', error);
    }
  });
  console.log(`[AuthCacheClear] Cleared ${cacheClearFunctions.length} caches`);
}

